import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, X, FileSignature, Send, RotateCcw, Ban, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { CompanyPicker, ContactSelect } from './CRMPickers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-700', Sent: 'bg-blue-100 text-blue-700', Viewed: 'bg-indigo-100 text-indigo-700',
  Signed: 'bg-emerald-100 text-emerald-700', Declined: 'bg-red-100 text-red-700', Expired: 'bg-amber-100 text-amber-700',
};
export const AgreementStatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{status || 'Draft'}</span>
);

const AGREEMENT_TYPE_LABELS = { client_recruiting: 'Client Recruiting Agreement', split_fee: 'Split-Fee Agreement' };
const TYPE_COLORS = { client_recruiting: 'bg-slate-100 text-slate-700', split_fee: 'bg-purple-100 text-purple-700' };
export const AgreementTypeBadge = ({ type }) => {
  const t = type || 'client_recruiting';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[t]}`} data-testid={`agreement-type-badge-${t}`}>{AGREEMENT_TYPE_LABELS[t] || t}</span>;
};

const CRMAgreements = ({ embedded = false, navParams = {} }) => {
  const [config, setConfig] = useState({ statuses: [], defaults: {} });
  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [awaitingOnly, setAwaitingOnly] = useState(() => navParams.awaiting === 'true');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchWithAuth(`${API_URL}/api/crm/agreements/config`).then(setConfig).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100 });
    if (statusFilter) params.set('status', statusFilter);
    if (awaitingOnly) params.set('awaiting_action', 'true');
    fetchWithAuth(`${API_URL}/api/crm/agreements?${params.toString()}`)
      .then(d => { setAgreements(d.agreements); setTotal(d.total); }).catch(() => {}).finally(() => setLoading(false));
  }, [statusFilter, awaitingOnly]);

  useEffect(() => { load(); }, [load]);

  return (
    <IntelligenceLayout title="Agreements" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-agreements-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Agreements</h1>
            <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} client agreements · defaults: {config.defaults?.fee_percentage}% fee, {config.defaults?.guarantee_period_days}-day guarantee, {config.defaults?.payment_terms}</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-agreement-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors">
            <Plus className="h-4 w-4" /> New Agreement
          </button>
        </div>

        <div className="flex items-center flex-wrap gap-2" data-testid="agreements-filters">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="agreement-status-filter"
            className="text-xs font-medium px-3 py-2 border border-gray-200 rounded-lg text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]">
            <option value="">All statuses</option>
            {config.statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="awaiting-action-toggle">
            <input type="checkbox" checked={awaitingOnly} onChange={e => setAwaitingOnly(e.target.checked)} className="rounded" /> Awaiting signature/action
          </label>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : agreements.length === 0 ? (
            <p className="text-sm text-gray-400 p-8 text-center" data-testid="agreements-empty-state">No agreements match these filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid="agreements-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2.5 px-4">Company</th>
                  <th className="text-left py-2.5 px-4">Type</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-left py-2.5 px-4">Fee %</th>
                  <th className="text-left py-2.5 px-4">Guarantee</th>
                  <th className="text-left py-2.5 px-4">Payment Terms</th>
                  <th className="text-left py-2.5 px-4">Sent</th>
                  <th className="text-left py-2.5 px-4">Signed</th>
                </tr>
              </thead>
              <tbody>
                {agreements.map(a => (
                  <tr key={a.id} onClick={() => setSelected(a)} data-testid={`agreement-row-${a.id}`}
                    className="border-t border-gray-100 hover:bg-[#fdf9ef] cursor-pointer transition-colors">
                    <td className="py-2.5 px-4 font-medium text-[#0A1F44]">{a.company_name || '—'}</td>
                    <td className="py-2.5 px-4"><AgreementTypeBadge type={a.agreement_type} /></td>
                    <td className="py-2.5 px-4"><AgreementStatusBadge status={a.status} /></td>
                    <td className="py-2.5 px-4 text-gray-600">{a.agreement_type === 'split_fee' ? '—' : `${a.fee_percentage}%`}</td>
                    <td className="py-2.5 px-4 text-gray-600">{a.agreement_type === 'split_fee' ? '—' : `${a.guarantee_period_days} days`}</td>
                    <td className="py-2.5 px-4 text-gray-600">{a.agreement_type === 'split_fee' ? '—' : a.payment_terms}</td>
                    <td className="py-2.5 px-4 text-gray-400">{a.date_sent || '—'}</td>
                    <td className="py-2.5 px-4 text-gray-400">{a.date_signed || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && <CreateAgreementModal config={config} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selected && <AgreementDetailModal agreement={selected} config={config} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); load(); }} />}
    </IntelligenceLayout>
  );
};

const CreateAgreementModal = ({ config, onClose, onCreated }) => {
  const [company, setCompany] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [agreementType, setAgreementType] = useState('client_recruiting');
  const [form, setForm] = useState({
    fee_percentage: config.defaults?.fee_percentage ?? 20,
    guarantee_period_days: config.defaults?.guarantee_period_days ?? 90,
    payment_terms: config.defaults?.payment_terms ?? 'Net 30',
    notes: '', document_reference_url: '',
    split_fee_partner_name: '', split_fee_partner_contact_name: '', split_fee_partner_email: '',
    split_fee_omnia_percentage: '', split_fee_partner_percentage: '', split_fee_remittance_terms: '',
  });
  const [saving, setSaving] = useState(false);

  const isSplitFee = agreementType === 'split_fee';

  const changeType = (nextType) => {
    // Switching types clears whichever fields don't apply to the new type,
    // so no stale/inappropriate values carry over.
    if (nextType === 'split_fee') {
      setContactId(null);
      setForm(f => ({ ...f, fee_percentage: config.defaults?.fee_percentage ?? 20, guarantee_period_days: config.defaults?.guarantee_period_days ?? 90, payment_terms: config.defaults?.payment_terms ?? 'Net 30' }));
    } else {
      setForm(f => ({ ...f, split_fee_partner_name: '', split_fee_partner_contact_name: '', split_fee_partner_email: '', split_fee_omnia_percentage: '', split_fee_partner_percentage: '', split_fee_remittance_terms: '' }));
    }
    setAgreementType(nextType);
  };

  const submit = async () => {
    if (!company) { toast.error('Select a company'); return; }
    if (isSplitFee) {
      const required = [
        ['split_fee_partner_name', 'Partner agency legal name'],
        ['split_fee_partner_contact_name', 'Partner agency contact name'],
        ['split_fee_partner_email', 'Partner agency email'],
        ['split_fee_omnia_percentage', 'Omnia fee share %'],
        ['split_fee_partner_percentage', 'Partner agency fee share %'],
        ['split_fee_remittance_terms', 'Partner remittance terms'],
      ];
      const missing = required.find(([key]) => !form[key]);
      if (missing) { toast.error(`${missing[1]} is required for a Split-Fee Agreement`); return; }
    }
    setSaving(true);
    try {
      const payload = { company_id: company.id, agreement_type: agreementType, notes: form.notes, document_reference_url: form.document_reference_url };
      if (isSplitFee) {
        Object.assign(payload, {
          split_fee_partner_name: form.split_fee_partner_name,
          split_fee_partner_contact_name: form.split_fee_partner_contact_name,
          split_fee_partner_email: form.split_fee_partner_email,
          split_fee_omnia_percentage: Number(form.split_fee_omnia_percentage),
          split_fee_partner_percentage: Number(form.split_fee_partner_percentage),
          split_fee_remittance_terms: form.split_fee_remittance_terms,
        });
      } else {
        Object.assign(payload, {
          primary_contact_id: contactId, fee_percentage: form.fee_percentage,
          guarantee_period_days: form.guarantee_period_days, payment_terms: form.payment_terms,
        });
      }
      await fetchWithAuth(`${API_URL}/api/crm/agreements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      toast.success('Agreement created');
      onCreated();
    } catch (e) {
      toast.error('Could not create agreement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-agreement-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><FileSignature className="h-4 w-4" /> New Agreement</h3>
          <button onClick={onClose} data-testid="close-create-agreement-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Agreement Type</label>
          <select value={agreementType} onChange={e => changeType(e.target.value)} data-testid="agreement-type-select"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {(config.agreement_types || ['client_recruiting', 'split_fee']).map(t => (
              <option key={t} value={t}>{AGREEMENT_TYPE_LABELS[t] || t}</option>
            ))}
          </select>
        </div>
        <CompanyPicker value={company} onChange={setCompany} testId="agreement-company-picker" />
        {isSplitFee ? (
          <div className="space-y-2" data-testid="split-fee-create-fields">
            <input value={form.split_fee_partner_name} onChange={e => setForm({ ...form, split_fee_partner_name: e.target.value })}
              placeholder="Partner agency legal name" data-testid="split-fee-partner-name-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            <input value={form.split_fee_partner_contact_name} onChange={e => setForm({ ...form, split_fee_partner_contact_name: e.target.value })}
              placeholder="Partner agency contact name" data-testid="split-fee-partner-contact-name-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            <input type="email" value={form.split_fee_partner_email} onChange={e => setForm({ ...form, split_fee_partner_email: e.target.value })}
              placeholder="Partner agency email" data-testid="split-fee-partner-email-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={form.split_fee_omnia_percentage} onChange={e => setForm({ ...form, split_fee_omnia_percentage: e.target.value })}
                placeholder="Omnia fee share %" data-testid="split-fee-omnia-percentage-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input type="number" value={form.split_fee_partner_percentage} onChange={e => setForm({ ...form, split_fee_partner_percentage: e.target.value })}
                placeholder="Partner fee share %" data-testid="split-fee-partner-percentage-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <textarea value={form.split_fee_remittance_terms} onChange={e => setForm({ ...form, split_fee_remittance_terms: e.target.value })}
              placeholder="Partner remittance terms" rows={2} data-testid="split-fee-remittance-terms-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
        ) : (
          <>
            <ContactSelect companyId={company?.id} value={contactId} onChange={setContactId} testId="agreement-contact-select" placeholder="Primary contact (optional)" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={form.fee_percentage} onChange={e => setForm({ ...form, fee_percentage: Number(e.target.value) })}
                placeholder="Fee %" data-testid="agreement-fee-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input type="number" value={form.guarantee_period_days} onChange={e => setForm({ ...form, guarantee_period_days: Number(e.target.value) })}
                placeholder="Guarantee (days)" data-testid="agreement-guarantee-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })}
              placeholder="Payment terms" data-testid="agreement-payment-terms-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </>
        )}
        <input value={form.document_reference_url} onChange={e => setForm({ ...form, document_reference_url: e.target.value })}
          placeholder="Document/reference URL (optional)" data-testid="agreement-doc-url-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes"
          data-testid="agreement-notes-input" rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <button onClick={submit} disabled={saving} data-testid="create-agreement-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Agreement'}
        </button>
      </div>
    </div>
  );
};

const downloadAuthFile = async (url, filename) => {
  try {
    const token = localStorage.getItem('backoffice_token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (e) {
    toast.error(e.message || 'Download failed');
  }
};

const postAgreementAction = async (agreementId, action) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(`${API_URL}/api/crm/agreements/${agreementId}/${action}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const ConfirmRow = ({ label, value }) => (
  <div className="flex justify-between gap-2"><span className="text-gray-400">{label}</span><span className="text-[#0A1F44] font-medium text-right">{value || '—'}</span></div>
);

const AgreementDetailModal = ({ agreement, config, onClose, onUpdated }) => {
  const [form, setForm] = useState({ ...agreement });
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState(null);
  const [companyLocation, setCompanyLocation] = useState(null);
  const [boldsignConfigured, setBoldsignConfigured] = useState(false);
  const [splitFeeConfigured, setSplitFeeConfigured] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'send' | 'remind' | 'revoke' | null

  const isSplitFee = (agreement.agreement_type || 'client_recruiting') === 'split_fee';

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/crm/boldsign/status`).then(r => { setBoldsignConfigured(!!r.configured); setSplitFeeConfigured(!!r.split_fee_configured); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isSplitFee) return;
    if (agreement.company_id) {
      fetchWithAuth(`${API_URL}/api/crm/companies/${agreement.company_id}`)
        .then(res => {
          setCompanyLocation(res.location || null);
          setContact(form.primary_contact_id ? (res.contacts || []).find(c => c.id === form.primary_contact_id) || null : null);
        })
        .catch(() => {});
    } else {
      setContact(null);
    }
  }, [form.primary_contact_id, agreement.company_id, isSplitFee]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status, date_sent: form.date_sent, date_signed: form.date_signed, effective_date: form.effective_date,
        notes: form.notes, document_reference_url: form.document_reference_url,
      };
      if (isSplitFee) {
        Object.assign(payload, {
          split_fee_partner_name: form.split_fee_partner_name || null,
          split_fee_partner_contact_name: form.split_fee_partner_contact_name || null,
          split_fee_partner_email: form.split_fee_partner_email || null,
          split_fee_omnia_percentage: form.split_fee_omnia_percentage !== '' && form.split_fee_omnia_percentage != null ? Number(form.split_fee_omnia_percentage) : null,
          split_fee_partner_percentage: form.split_fee_partner_percentage !== '' && form.split_fee_partner_percentage != null ? Number(form.split_fee_partner_percentage) : null,
          split_fee_remittance_terms: form.split_fee_remittance_terms || null,
        });
      } else {
        Object.assign(payload, {
          fee_percentage: Number(form.fee_percentage), guarantee_period_days: Number(form.guarantee_period_days),
          payment_terms: form.payment_terms, primary_contact_id: form.primary_contact_id || null,
        });
      }
      await fetchWithAuth(`${API_URL}/api/crm/agreements/${agreement.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      toast.success('Agreement updated');
      onUpdated();
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const signerName = contact ? (contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()) : null;
  const clientContact = signerName ? (contact?.title ? `${signerName}, ${contact.title}` : signerName) : null;
  const hasUnsavedSignerChange = (form.primary_contact_id || null) !== (agreement.primary_contact_id || null);

  const SPLIT_FEE_REQUIRED = [
    ['split_fee_partner_name', 'Partner agency legal name'],
    ['split_fee_partner_contact_name', 'Partner agency contact name'],
    ['split_fee_partner_email', 'Partner agency email'],
    ['split_fee_omnia_percentage', 'Omnia fee share %'],
    ['split_fee_partner_percentage', 'Partner agency fee share %'],
    ['split_fee_remittance_terms', 'Partner remittance terms'],
  ];

  let missingReasons = [];
  let canSend;
  if (isSplitFee) {
    if (!splitFeeConfigured) missingReasons.push('BoldSign split-fee template is not configured yet');
    SPLIT_FEE_REQUIRED.forEach(([key, label]) => { if (!form[key]) missingReasons.push(`${label} is required`); });
    canSend = splitFeeConfigured && SPLIT_FEE_REQUIRED.every(([key]) => !!form[key]);
  } else {
    if (!boldsignConfigured) missingReasons.push('BoldSign is not configured yet');
    if (!companyLocation) missingReasons.push('Add a company address (Location) in Companies before sending');
    if (!form.primary_contact_id) missingReasons.push('Select a client signer (Role 1) below');
    else if (!contact?.email) missingReasons.push('The selected client signer has no email on file');
    else if (hasUnsavedSignerChange) missingReasons.push('Save your changes, then reopen this agreement to send');
    canSend = boldsignConfigured && !!contact?.email && !!companyLocation && !hasUnsavedSignerChange;
  }

  const doSendForSignature = async () => {
    setBusyAction('send');
    try {
      await postAgreementAction(agreement.id, 'send-for-signature');
      toast.success('Sent for signature');
      setShowSendConfirm(false);
      onUpdated();
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setBusyAction(null);
    }
  };

  const doRemind = async () => {
    setBusyAction('remind');
    try { await postAgreementAction(agreement.id, 'remind'); toast.success('Reminder sent'); }
    catch (e) { toast.error(e.message || 'Reminder failed'); }
    finally { setBusyAction(null); }
  };

  const doRevoke = async () => {
    setBusyAction('revoke');
    try { await postAgreementAction(agreement.id, 'revoke'); toast.success('Signature request revoked'); onUpdated(); }
    catch (e) { toast.error(e.message || 'Revoke failed'); }
    finally { setBusyAction(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="agreement-detail-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44]">{agreement.company_name}</h3>
            <AgreementTypeBadge type={agreement.agreement_type} />
          </div>
          <button onClick={onClose} data-testid="close-agreement-detail"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-2" data-testid="agreement-signing-section">
          {isSplitFee ? (
            <div className="space-y-2" data-testid="split-fee-detail-fields">
              <div><label className="text-xs font-medium text-gray-500">Partner Agency Legal Name</label>
                <input value={form.split_fee_partner_name || ''} onChange={e => setForm({ ...form, split_fee_partner_name: e.target.value })} data-testid="split-fee-detail-partner-name" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              <div><label className="text-xs font-medium text-gray-500">Partner Agency Contact Name</label>
                <input value={form.split_fee_partner_contact_name || ''} onChange={e => setForm({ ...form, split_fee_partner_contact_name: e.target.value })} data-testid="split-fee-detail-partner-contact-name" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              <div><label className="text-xs font-medium text-gray-500">Partner Agency Email</label>
                <input type="email" value={form.split_fee_partner_email || ''} onChange={e => setForm({ ...form, split_fee_partner_email: e.target.value })} data-testid="split-fee-detail-partner-email" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs font-medium text-gray-500">Omnia Fee Share %</label>
                  <input type="number" value={form.split_fee_omnia_percentage ?? ''} onChange={e => setForm({ ...form, split_fee_omnia_percentage: e.target.value })} data-testid="split-fee-detail-omnia-percentage" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
                <div><label className="text-xs font-medium text-gray-500">Partner Fee Share %</label>
                  <input type="number" value={form.split_fee_partner_percentage ?? ''} onChange={e => setForm({ ...form, split_fee_partner_percentage: e.target.value })} data-testid="split-fee-detail-partner-percentage" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              </div>
              <div><label className="text-xs font-medium text-gray-500">Partner Remittance Terms</label>
                <textarea value={form.split_fee_remittance_terms || ''} onChange={e => setForm({ ...form, split_fee_remittance_terms: e.target.value })} rows={2} data-testid="split-fee-detail-remittance-terms" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              <p className="text-xs text-gray-400 mt-1">Role 2 (Omnia Paratus signer) is added automatically when sent.</p>
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium text-gray-500">Client Signer — Role 1</label>
              <ContactSelect companyId={agreement.company_id} value={form.primary_contact_id} onChange={v => setForm({ ...form, primary_contact_id: v })}
                testId="agreement-signer-select" placeholder="Select client signer" />
              {form.primary_contact_id && (
                <p className="text-xs text-gray-500 mt-1" data-testid="agreement-signer-preview">
                  {contact ? (
                    <>Signer: <span className="font-medium text-[#0A1F44]">{signerName || contact.email}</span>{contact.email ? ` — ${contact.email}` : ' — no email on file'}</>
                  ) : 'Loading signer…'}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">Role 2 (Omnia Paratus signer) is added automatically when sent.</p>
            </div>
          )}
          {agreement.signature_provider_status && (
            <p className="text-xs text-gray-500" data-testid="agreement-provider-status">
              BoldSign status: <span className="font-medium text-[#0A1F44]">{agreement.signature_provider_status}</span>
            </p>
          )}
          {agreement.status === 'Draft' && !agreement.signature_request_id && !canSend && missingReasons.length > 0 && (
            <p className="text-xs text-amber-700 flex items-start gap-1.5" data-testid="send-for-signature-blocked-reasons">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>Can't send yet — {missingReasons.join('; ')}.</span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {agreement.status === 'Draft' && !agreement.signature_request_id && (
              <button onClick={() => setShowSendConfirm(true)} disabled={!canSend} data-testid="send-for-signature-btn"
                title={missingReasons.join('; ')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0A1F44] text-white disabled:opacity-40">
                <Send className="h-3.5 w-3.5" /> Send for Signature
              </button>
            )}
            {agreement.signature_request_id && agreement.status === 'Sent' && (
              <>
                <button onClick={doRemind} disabled={busyAction !== null} data-testid="remind-signer-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50">
                  {busyAction === 'remind' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Send Reminder
                </button>
                <button onClick={doRevoke} disabled={busyAction !== null} data-testid="revoke-signature-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 disabled:opacity-50">
                  {busyAction === 'revoke' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Revoke
                </button>
              </>
            )}
            {agreement.status === 'Signed' && (
              <button onClick={() => downloadAuthFile(`${API_URL}/api/crm/agreements/${agreement.id}/signed-pdf`, `agreement-${agreement.company_name}-signed.pdf`)}
                data-testid="download-signed-agreement-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600">
                <Download className="h-3.5 w-3.5" /> Download Signed Agreement
              </button>
            )}
            {agreement.signature_request_id && (
              <button onClick={() => downloadAuthFile(`${API_URL}/api/crm/agreements/${agreement.id}/audit-trail`, `agreement-${agreement.company_name}-audit-trail.pdf`)}
                data-testid="download-audit-trail-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600">
                <Download className="h-3.5 w-3.5" /> Download Audit Trail
              </button>
            )}
          </div>
        </div>

        <label className="text-xs font-medium text-gray-500">Status</label>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="agreement-detail-status"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
          {config.statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-medium text-gray-500">Date Sent</label>
            <input type="date" value={form.date_sent || ''} onChange={e => setForm({ ...form, date_sent: e.target.value })} data-testid="agreement-detail-date-sent" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Date Signed</label>
            <input type="date" value={form.date_signed || ''} onChange={e => setForm({ ...form, date_signed: e.target.value })} data-testid="agreement-detail-date-signed" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
        </div>
        <div><label className="text-xs font-medium text-gray-500">Effective Date</label>
          <input type="date" value={form.effective_date || ''} onChange={e => setForm({ ...form, effective_date: e.target.value })} data-testid="agreement-detail-effective-date" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
        {!isSplitFee && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-medium text-gray-500">Fee %</label>
                <input type="number" value={form.fee_percentage} onChange={e => setForm({ ...form, fee_percentage: e.target.value })} data-testid="agreement-detail-fee" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
              <div><label className="text-xs font-medium text-gray-500">Guarantee (days)</label>
                <input type="number" value={form.guarantee_period_days} onChange={e => setForm({ ...form, guarantee_period_days: e.target.value })} data-testid="agreement-detail-guarantee" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
            </div>
            <div><label className="text-xs font-medium text-gray-500">Payment Terms</label>
              <input value={form.payment_terms || ''} onChange={e => setForm({ ...form, payment_terms: e.target.value })} data-testid="agreement-detail-payment-terms" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          </>
        )}
        <div><label className="text-xs font-medium text-gray-500">Document/Reference URL</label>
          <input value={form.document_reference_url || ''} onChange={e => setForm({ ...form, document_reference_url: e.target.value })} data-testid="agreement-detail-doc-url" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
        <div><label className="text-xs font-medium text-gray-500">Notes</label>
          <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="agreement-detail-notes" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
        {agreement.job_orders?.length > 0 && (
          <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">
            Linked Job Orders: {agreement.job_orders.map(j => j.job_title).join(', ')}
          </div>
        )}
        <button onClick={save} disabled={saving} data-testid="save-agreement-detail"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {showSendConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="send-signature-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-3">
            <h4 className="text-base font-bold text-[#0A1F44] flex items-center gap-1.5"><Send className="h-4 w-4 text-[#D4AF37]" /> Confirm Send for Signature</h4>
            {isSplitFee ? (
              <>
                <p className="text-xs text-gray-500">This will create a real BoldSign signature request on the Split-Fee Agreement template. These values will be prefilled — review before confirming.</p>
                <div className="space-y-1.5 text-sm bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <ConfirmRow label="Partner Legal Name" value={form.split_fee_partner_name} />
                  <ConfirmRow label="Partner Contact" value={form.split_fee_partner_contact_name} />
                  <ConfirmRow label="Partner Email" value={form.split_fee_partner_email} />
                  <ConfirmRow label="Omnia Fee Share" value={form.split_fee_omnia_percentage != null && form.split_fee_omnia_percentage !== '' ? `${form.split_fee_omnia_percentage}%` : null} />
                  <ConfirmRow label="Partner Fee Share" value={form.split_fee_partner_percentage != null && form.split_fee_partner_percentage !== '' ? `${form.split_fee_partner_percentage}%` : null} />
                  <ConfirmRow label="Remittance Terms" value={form.split_fee_remittance_terms} />
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">This will create a real BoldSign signature request. These 6 values will be prefilled on the client agreement — review before confirming.</p>
                <div className="space-y-1.5 text-sm bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <ConfirmRow label="Client Legal Name" value={agreement.company_name} />
                  <ConfirmRow label="Client Address" value={companyLocation} />
                  <ConfirmRow label="Client Contact" value={clientContact} />
                  <ConfirmRow label="Fee %" value={`${agreement.fee_percentage}%`} />
                  <ConfirmRow label="Payment Terms" value={agreement.payment_terms} />
                  <ConfirmRow label="Guarantee Period" value={`${agreement.guarantee_period_days} days`} />
                  <ConfirmRow label="Signer Email" value={contact?.email} />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowSendConfirm(false)} disabled={busyAction !== null} data-testid="send-signature-cancel-btn"
                className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium disabled:opacity-50">Cancel</button>
              <button onClick={doSendForSignature} disabled={busyAction !== null} data-testid="send-signature-confirm-btn"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {busyAction === 'send' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {busyAction === 'send' ? 'Sending…' : 'Confirm Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMAgreements;
